import { type DocumentNode, print } from 'graphql';

import { GraphQLCache } from './cache';
import { GraphQLClientError } from './errors';
import { GraphQLTransport } from './transport';
import {
  type CacheConfig,
  type FetchPolicy,
  type GraphQLClientConfig,
  type GraphQLRequestBody,
  type MutationOptions,
  type MutationResult,
  NetworkStatus,
  type QueryOptions,
  type QueryResult,
  type RequestContext,
  type RetryConfig,
} from './types';

/**
 * Extract operation name from a DocumentNode.
 */
function getOperationName(doc: DocumentNode): string | undefined {
  for (const definition of doc.definitions) {
    if (definition.kind === 'OperationDefinition' && definition.name) {
      return definition.name.value;
    }
  }
  return undefined;
}

/**
 * Convert query to string format if it's a DocumentNode.
 */
function queryToString(query: string | DocumentNode): string {
  if (typeof query === 'string') {
    return query;
  }
  return print(query);
}

/**
 * Main GraphQL client class.
 * Provides query and mutation methods with caching and retry support.
 */
export class GraphQLClient {
  private readonly endpoint: string;
  private readonly cache: GraphQLCache;
  private readonly transport: GraphQLTransport;

  constructor(config: GraphQLClientConfig) {
    this.endpoint = config.endpoint;
    this.cache = new GraphQLCache(config.cache);
    this.transport = new GraphQLTransport({
      fetcher: config.fetcher,
      headers: config.headers,
      retry: config.retry,
    });
  }

  /**
   * Execute a GraphQL query.
   */
  async query<TData = unknown, TVariables extends object = Record<string, unknown>>(
    options: QueryOptions<TVariables>,
  ): Promise<QueryResult<TData>> {
    const queryString = queryToString(options.query);
    const fetchPolicy: FetchPolicy = options.fetchPolicy ?? 'cache-first';
    const cacheKey = this.cache.generateKey(
      queryString,
      options.variables as Record<string, unknown> | undefined,
    );

    if (this.cache.shouldReadFromCache(fetchPolicy)) {
      const cached = this.cache.get<TData>(cacheKey);
      if (cached) {
        return {
          data: cached.data,
          loading: false,
          networkStatus: NetworkStatus.Ready,
        };
      }

      if (fetchPolicy === 'cache-only') {
        throw new GraphQLClientError('No cached data available for cache-only policy');
      }
    }

    const operationName =
      typeof options.query === 'string' ? undefined : getOperationName(options.query);

    const body: GraphQLRequestBody = {
      query: queryString,
      variables: options.variables,
      operationName,
    };

    const response = await this.transport.execute<TData>(
      this.endpoint,
      body,
      options.context,
    );

    if (response.errors?.length) {
      throw new GraphQLClientError(
        `GraphQL errors: ${response.errors.map((e) => e.message).join(', ')}`,
        { graphQLErrors: response.errors },
      );
    }

    if (response.data === undefined) {
      throw new GraphQLClientError('GraphQL response missing data');
    }

    if (this.cache.shouldWriteToCache(fetchPolicy)) {
      this.cache.set(cacheKey, response.data);
    }

    return {
      data: response.data,
      loading: false,
      networkStatus: NetworkStatus.Ready,
    };
  }

  /**
   * Execute a GraphQL mutation.
   */
  async mutate<TData = unknown, TVariables extends object = Record<string, unknown>>(
    options: MutationOptions<TVariables>,
  ): Promise<MutationResult<TData>> {
    const mutationString = queryToString(options.mutation);
    const operationName =
      typeof options.mutation === 'string'
        ? undefined
        : getOperationName(options.mutation);

    const body: GraphQLRequestBody = {
      query: mutationString,
      variables: options.variables,
      operationName,
    };

    const response = await this.transport.execute<TData>(
      this.endpoint,
      body,
      options.context,
    );

    if (response.errors?.length) {
      throw new GraphQLClientError(
        `GraphQL mutation errors: ${response.errors.map((e) => e.message).join(', ')}`,
        { graphQLErrors: response.errors },
      );
    }

    return {
      data: response.data,
    };
  }

  /**
   * Clear the cache.
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Evict a specific cache entry.
   */
  evictCache(key: string): void {
    this.cache.evict(key);
  }
}

/**
 * Create a GraphQL client with the given configuration.
 */
export function createGraphQLClient(config: GraphQLClientConfig): GraphQLClient {
  return new GraphQLClient(config);
}

export {
  type GraphQLClientConfig,
  type CacheConfig,
  type RetryConfig,
  type RequestContext,
};
